/* ============================================================
   MIAMI MAXLIFE — MOTION SYSTEM (Shopify port of main.js)
   Loader · Lenis smooth scroll · GSAP/ScrollTrigger choreography
   Theme Editor: no loader, no Lenis, no page transitions; motion
   re-initialises per section on shopify:section:load.
   ============================================================ */
(function () {
  'use strict';

  var docEl = document.documentElement;
  docEl.classList.add('js');

  var cfg = window.MML || {};
  var designMode = !!cfg.designMode || !!(window.Shopify && window.Shopify.designMode);
  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isDesktop = window.matchMedia('(min-width: 900px)').matches;
  var finePointer = window.matchMedia('(pointer: fine)').matches;

  var MOTION = { easeOut: 'power3.out', easeInOut: 'power2.inOut', text: 0.9, img: 1.1, card: 0.8, stagger: 0.09, loaderMin: 1700, loaderMax: 4000 };

  /* mark elements so section reloads never double-bind */
  function once(el, key) { key = 'mml' + key; if (el.dataset[key]) return false; el.dataset[key] = '1'; return true; }

  /* ---------------- LOADER ---------------- */
  var loader = document.getElementById('loader');
  var seen = false;
  try { seen = sessionStorage.getItem('mml-loaded') === '1'; } catch (e) {}
  function lockScroll(on) { document.body.classList.toggle('is-loading', on); }

  function removeLoader(instant) {
    if (!loader || loader.hidden) return;
    var done = function () { loader.hidden = true; lockScroll(false); startHeroIntro(); };
    if (instant || prefersReduced || !window.gsap) { loader.style.display = 'none'; done(); return; }
    gsap.timeline({ onComplete: done })
      .to('.loader__veil', { opacity: 1, duration: 0.5, ease: MOTION.easeInOut })
      .to('.loader__inner', { opacity: 0, y: -14, duration: 0.55, ease: MOTION.easeInOut }, '<')
      .to(loader, { opacity: 0, duration: 0.7, ease: MOTION.easeInOut }, '-=0.25');
  }

  function playLoader() {
    lockScroll(true);
    var assetsReady = false, minDone = false, finished = false;
    function tryFinish(force) {
      if (finished) return;
      if (force || (assetsReady && minDone)) { finished = true; try { sessionStorage.setItem('mml-loaded', '1'); } catch (e) {} removeLoader(false); }
    }
    /* Power-on: the bar drops in, the ring sweeps round from it, the five segments charge up clockwise,
       the M settles into focus, then a light sweep crosses the mark and the wordmark wipes in. */
    gsap.timeline({ defaults: { ease: MOTION.easeOut } })
      .fromTo('.lm--bar', { opacity: 0, y: -16 }, { opacity: 1, y: 0, duration: 0.4, ease: 'power3.out' })
      .to('.lm--arc', { '--a': '172deg', duration: 0.62, ease: MOTION.easeInOut }, 0.16)
      .fromTo('.lm--seg', { opacity: 0, scale: 1.05 }, { opacity: 1, scale: 1, duration: 0.2, stagger: 0.065, ease: 'power2.out' }, 0.42)
      .fromTo('.lm--m', { opacity: 0, y: 10, scale: 0.94, filter: 'blur(6px)' }, { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)', duration: 0.5, ease: 'power3.out' }, 0.66)
      .to('.lm--glow', { opacity: 0.55, duration: 0.3, ease: 'power2.out' }, 0.82)
      .to('.lm--glow', { opacity: 0.2, duration: 0.7, ease: MOTION.easeInOut }, 1.1)
      .fromTo('.lm--sheen', { opacity: 1, backgroundPosition: '140% 0' }, { backgroundPosition: '-40% 0', duration: 0.75, ease: MOTION.easeInOut }, 0.86)
      .to('.loader__veil', { opacity: 0.65, duration: 0.7, ease: MOTION.easeInOut }, 0.45)
      .to('.loader__word', { clipPath: 'inset(0 0% 0 0)', duration: 0.6, ease: 'power3.inOut' }, 0.78)
      .to('.loader__phrase', { opacity: 1, duration: 0.5 }, 1.05);
    gsap.to('.loader__bar-fill', { scaleX: 1, duration: MOTION.loaderMin / 1000, ease: MOTION.easeInOut, onComplete: function () { minDone = true; tryFinish(); } });
    var critical = (cfg.heroCritical || []).slice();
    if (!critical.length) { assetsReady = true; }
    var left = critical.length;
    critical.forEach(function (src) { var im = new Image(); im.onload = im.onerror = function () { if (--left <= 0) { assetsReady = true; tryFinish(); } }; im.src = src; });
    setTimeout(function () { tryFinish(true); }, MOTION.loaderMax); // failsafe: the loader can never trap the user
  }
  // Belt and braces: even if GSAP fails to load, unlock after the failsafe window.
  setTimeout(function () { if (document.body.classList.contains('is-loading')) { lockScroll(false); if (loader) loader.hidden = true; } }, MOTION.loaderMax + 800);

  /* ---------------- HERO INTRO ---------------- */
  var heroIntroPlayed = false;
  function startHeroIntro() {
    if (heroIntroPlayed) return;
    heroIntroPlayed = true;
    if (prefersReduced || !window.gsap) return;
    if (!document.querySelector('.hero')) return;
    var tl = gsap.timeline({ defaults: { ease: MOTION.easeOut } });
    tl.to('[data-hero-title] .line', { y: 0, duration: 1.05, stagger: 0.12 }, 0.1)
      .to('[data-hero-fade]', { opacity: 1, y: 0, duration: MOTION.text, stagger: 0.12 }, 0.45)
      .to('.hero__bottle', { opacity: 1, y: 0, duration: 1.3, stagger: 0.1, onComplete: startBreathing }, 0.35)
      .to('.trail', { strokeDashoffset: 0, duration: 2.6, ease: MOTION.easeInOut, stagger: 0.25 }, 0.5);
  }
  function startBreathing() {
    document.querySelectorAll('.hero__bottle').forEach(function (b, i) {
      gsap.to(b, { y: '-=7', duration: 3.6 + i * 0.5, yoyo: true, repeat: -1, ease: 'sine.inOut', delay: i * 0.4 });
    });
  }

  /* ---------------- LENIS ---------------- */
  var lenis = null;
  function initSmoothScroll() {
    if (!window.Lenis || designMode) return;
    lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1, smoothWheel: true });
    window.__mmlLenis = lenis;
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(function (t) { lenis.raf(t * 1000); });
    gsap.ticker.lagSmoothing(0);
  }
  function wireAnchors(root) {
    (root || document).querySelectorAll('a[href^="#"], a[href^="/#"]').forEach(function (a) {
      if (!once(a, 'Anchor')) return;
      a.addEventListener('click', function (ev) {
        var href = a.getAttribute('href');
        var id = href.charAt(0) === '/' ? href.slice(1) : href;
        if (id.length < 2) return;
        var target = document.querySelector(id);
        if (!target) return; // e.g. /#science from another page → normal navigation
        ev.preventDefault();
        closeMenu();
        if (lenis) lenis.scrollTo(target, { offset: -70, duration: 1.4 });
        else target.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth' });
      });
    });
  }

  /* ---------------- REVEALS ---------------- */
  function initReveals(root) {
    root = root || document;
    root.querySelectorAll('[data-reveal="lines"]').forEach(function (el) {
      if (!once(el, 'Rev')) return;
      gsap.set(el, { opacity: 1 });
      gsap.to(el.querySelectorAll('.line'), { y: 0, duration: 1.0, ease: MOTION.easeOut, stagger: 0.12, scrollTrigger: { trigger: el, start: 'top 82%' } });
    });
    root.querySelectorAll('[data-reveal="text"]').forEach(function (el) {
      if (!once(el, 'Rev')) return;
      gsap.to(el, { opacity: 1, y: 0, duration: MOTION.text, ease: MOTION.easeOut, scrollTrigger: { trigger: el, start: 'top 88%' } });
    });
    root.querySelectorAll('[data-reveal="img"]').forEach(function (el) {
      if (!once(el, 'Rev')) return;
      gsap.fromTo(el, { opacity: 0, y: 30, filter: 'blur(6px)' }, { opacity: 1, y: 0, filter: 'blur(0px)', duration: MOTION.img, ease: MOTION.easeOut, scrollTrigger: { trigger: el, start: 'top 85%' } });
    });
    var groups = new Map();
    root.querySelectorAll('[data-reveal="card"]').forEach(function (el) {
      if (!once(el, 'Rev')) return;
      var parent = el.parentElement.closest('ul, .cards, .trust, .ing, .quotes__rail') || el.parentElement;
      if (!groups.has(parent)) groups.set(parent, []);
      groups.get(parent).push(el);
    });
    groups.forEach(function (els, parent) {
      gsap.to(els, { opacity: 1, y: 0, duration: MOTION.card, ease: MOTION.easeOut, stagger: MOTION.stagger, scrollTrigger: { trigger: parent, start: 'top 88%' } });
    });
  }

  /* ---------------- HERO PARALLAX ---------------- */
  function initHeroParallax() {
    var hero = document.querySelector('.hero');
    if (!hero || !once(hero, 'Par')) return;
    var bg = document.querySelector('[data-hero-bg] img');
    if (bg) gsap.fromTo(bg, { scale: 1.08, y: 0 }, { scale: 1.0, y: '6%', ease: 'none', scrollTrigger: { trigger: '.hero', start: 'top top', end: 'bottom top', scrub: true } });
    gsap.to('.hero__stage', { y: '-8%', ease: 'none', scrollTrigger: { trigger: '.hero', start: 'top top', end: 'bottom top', scrub: true } });
    gsap.to('.hero__copy', { y: '-14%', opacity: 0.25, ease: 'none', scrollTrigger: { trigger: '.hero', start: '30% top', end: 'bottom top', scrub: true } });
    if (isDesktop && finePointer) {
      var stage = document.querySelector('[data-hero-stage]');
      if (!stage) return;
      var setters = [];
      stage.querySelectorAll('.hero__bottle').forEach(function (b) {
        var d = parseFloat(b.getAttribute('data-depth') || '0.5');
        setters.push({ x: gsap.quickTo(b, 'x', { duration: 1.2, ease: 'power3.out' }), depth: d });
      });
      window.addEventListener('pointermove', function (e) {
        var rel = (e.clientX / window.innerWidth - 0.5);
        setters.forEach(function (s) { s.x(rel * 18 * s.depth); });
      }, { passive: true });
    }
  }

  /* ---------------- FEATURE ROTATOR ---------------- */
  function initFeatureRotator() {
    var stage = document.querySelector('[data-rotator]');
    if (!stage || !once(stage, 'Rot')) return;
    var frames = stage.querySelectorAll('.rot');
    var state = { idx: 0 };
    ScrollTrigger.create({
      trigger: '.feature', start: 'top 60%', end: 'bottom 30%', scrub: 0.6,
      onUpdate: function (self) {
        var p = self.progress;
        var idx = p < 0.38 ? 0 : (p < 0.72 ? 1 : 2);
        idx = Math.min(idx, frames.length - 1);
        if (idx !== state.idx && frames[idx]) { frames[state.idx].classList.remove('is-active'); frames[idx].classList.add('is-active'); state.idx = idx; }
        gsap.set(stage, { rotationZ: (p - 0.5) * 4, y: (0.5 - p) * 30, scale: 0.96 + 0.08 * Math.sin(p * Math.PI) });
      }
    });
    var orbitTween = gsap.to('.orbit svg', { rotation: 360, duration: 60, repeat: -1, ease: 'none' });
    ScrollTrigger.create({ trigger: '.feature', start: 'top bottom', end: 'bottom top', onToggle: function (self) { self.isActive ? orbitTween.play() : orbitTween.pause(); } });
  }

  /* ---------------- SECTION PARALLAX ---------------- */
  function initSectionParallax() {
    var fbg = document.querySelector('[data-parallax-bg] img');
    if (fbg && once(fbg, 'Par')) gsap.fromTo(fbg, { y: '-5%' }, { y: '5%', ease: 'none', scrollTrigger: { trigger: '.feature', start: 'top bottom', end: 'bottom top', scrub: true } });
    var sci = document.querySelector('.science__visual img');
    if (sci && once(sci, 'Par')) gsap.fromTo(sci, { y: '-4%' }, { y: '4%', ease: 'none', scrollTrigger: { trigger: '.science', start: 'top bottom', end: 'bottom top', scrub: true } });
    var drzImg = document.querySelector('.drz__portrait img');
    if (drzImg && once(drzImg, 'Par')) gsap.fromTo(drzImg, { y: '-3%' }, { y: '3%', ease: 'none', scrollTrigger: { trigger: '.drz', start: 'top bottom', end: 'bottom top', scrub: true } });
    gsap.utils.toArray('.bundle__b').forEach(function (b, i) {
      if (!once(b, 'Par')) return;
      gsap.from(b, { y: 26, opacity: 0, duration: 1, ease: MOTION.easeOut, delay: i * 0.08, scrollTrigger: { trigger: b.closest('.bundle') || '.bundle', start: 'top 75%' } });
    });
  }

  /* ---------------- HEADER ---------------- */
  function wireHeader() {
    var header = document.getElementById('header');
    if (!header || !once(header, 'Hdr')) return;
    var announceH = 34;
    function onScroll() { var y = window.scrollY || window.pageYOffset; header.classList.toggle('is-solid', y > announceH + 8); }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    if (window.ScrollTrigger && !designMode) {
      ['#collection', '#science', '#drz'].forEach(function (sel) {
        var sec = document.querySelector(sel);
        if (!sec) return;
        ScrollTrigger.create({
          trigger: sec, start: 'top 50%', end: 'bottom 50%',
          onToggle: function (self) {
            document.querySelectorAll('.nav__link').forEach(function (l) {
              var h = l.getAttribute('href') || '';
              if (h === sel || h === '/' + sel) l.classList.toggle('is-active', self.isActive);
            });
          }
        });
      });
    }
  }

  /* ---------------- MOBILE MENU ---------------- */
  function closeMenu() {
    var menuBtn = document.getElementById('menuBtn'), mmenu = document.getElementById('mobile-menu');
    if (!mmenu || !mmenu.classList.contains('is-open')) return;
    mmenu.classList.remove('is-open'); mmenu.setAttribute('aria-hidden', 'true');
    if (menuBtn) { menuBtn.classList.remove('is-open'); menuBtn.setAttribute('aria-expanded', 'false'); }
    if (lenis) lenis.start();
  }
  function wireMenu() {
    var menuBtn = document.getElementById('menuBtn'), mmenu = document.getElementById('mobile-menu');
    if (!menuBtn || !mmenu || !once(menuBtn, 'Menu')) return;
    menuBtn.addEventListener('click', function () {
      var open = mmenu.classList.toggle('is-open');
      mmenu.setAttribute('aria-hidden', String(!open));
      menuBtn.classList.toggle('is-open', open);
      menuBtn.setAttribute('aria-expanded', String(open));
      if (lenis) open ? lenis.stop() : lenis.start();
    });
    mmenu.querySelectorAll('a').forEach(function (a) { a.addEventListener('click', closeMenu); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeMenu(); });
  }

  /* ---------------- TESTIMONIAL RAIL ---------------- */
  function wireQuotesRail() {
    document.querySelectorAll('.quotes__rail-wrap').forEach(function (wrap) {
      if (!once(wrap, 'Rail')) return;
      var rail = wrap.querySelector('.quotes__rail'); if (!rail) return;
      var step = function () { var c = rail.querySelector('.quote-card'); return (c ? c.offsetWidth : 300) + 20; };
      var prev = wrap.querySelector('.rail-btn--prev'), next = wrap.querySelector('.rail-btn--next');
      if (prev) prev.addEventListener('click', function () { rail.scrollBy({ left: -step(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { rail.scrollBy({ left: step(), behavior: 'smooth' }); });
    });
  }

  /* ---------------- MAGNETIC BUTTONS ---------------- */
  function initMagnetic(root) {
    if (!isDesktop || !finePointer) return;
    (root || document).querySelectorAll('[data-magnetic]').forEach(function (btn) {
      if (!once(btn, 'Mag')) return;
      var xTo = gsap.quickTo(btn, 'x', { duration: 0.5, ease: 'power3.out' });
      var yTo = gsap.quickTo(btn, 'y', { duration: 0.5, ease: 'power3.out' });
      btn.addEventListener('pointermove', function (e) { var r = btn.getBoundingClientRect(); xTo((e.clientX - r.left - r.width / 2) * 0.18); yTo((e.clientY - r.top - r.height / 2) * 0.3); });
      btn.addEventListener('pointerleave', function () { xTo(0); yTo(0); });
    });
  }

  /* ---------------- BOOT ---------------- */
  function boot() {
    if (!window.gsap || !window.ScrollTrigger) {
      docEl.classList.remove('js');
      if (loader) loader.hidden = true;
      lockScroll(false);
      wireHeader(); wireMenu(); wireQuotesRail();
      return;
    }
    gsap.registerPlugin(ScrollTrigger);

    if (prefersReduced || designMode) {
      // Theme Editor and reduced motion: everything visible, no choreography.
      docEl.classList.remove('js');
      if (loader) loader.hidden = true;
      lockScroll(false);
      wireHeader(); wireMenu(); wireQuotesRail(); wireAnchors();
      return;
    }

    if (!loader || seen || document.visibilityState === 'hidden') {
      if (loader) loader.hidden = true;
      lockScroll(false);
      startHeroIntro();
    } else {
      playLoader();
    }

    initSmoothScroll();
    wireAnchors();
    wireHeader(); wireMenu(); wireQuotesRail();
    initReveals();
    initHeroParallax();
    initFeatureRotator();
    initSectionParallax();
    initMagnetic();
  }

  window.__mml = { initReveals: initReveals, initMagnetic: initMagnetic, wireAnchors: wireAnchors, wireQuotesRail: wireQuotesRail, closeMenu: closeMenu, MOTION: MOTION, once: once, isDesign: designMode, reduced: prefersReduced };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

  /* Theme Editor: keep the page usable after section changes. */
  document.addEventListener('shopify:section:load', function (e) {
    wireHeader(); wireMenu(); wireQuotesRail(); wireAnchors(e.target);
    if (window.ScrollTrigger) ScrollTrigger.refresh();
  });
  document.addEventListener('shopify:section:unload', function () { if (window.ScrollTrigger) ScrollTrigger.refresh(); });
})();
